import { useInfiniteQuery } from '@tanstack/react-query';

import { ActionItem, Period } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';
import { useLocale } from '~/hooks/useLocale';

const log = createLogger('useRemindersListAllQuery');

export const QUERY_KEY = 'remindersListAll';

export type RemindersListAllResponse = {
  reminders: {
    period: Period;
    date: Date;
    reminders: ActionItem[];
  }[];
  nextCursor?: string;
};

export function useRemindersListAllQuery() {
  const httpClient = useHttpClient();
  const tz = useLocale()?.timeZone ?? '';

  const { data, error, status, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useInfiniteQuery<null | RemindersListAllResponse>({
      queryKey: [QUERY_KEY],
      queryFn: async ({ pageParam = '' }) => {
        log.debug('Fetching all reminders');

        const cursor = encodeURIComponent((pageParam as string) ?? '');

        const queryString = `/review/listAllReminders?tz=${tz}&cursor=${cursor}`;

        const { reminders, nextCursor } = await httpClient.get<RemindersListAllResponse>(queryString);
        log.info('Fetched all reminders successfully', {
          reminderCount: reminders.length,
        });
        return { reminders, nextCursor };
      },
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
      initialPageParam: '',
      retry: (failureCount, error) =>
        determineRetryAndMaybeLog({ operation: 'fetch all reminders', failureCount, error, log }),
      enabled: httpClient.isAuthenticated,
    });

  return { data, error, status, fetchNextPage, isFetchingNextPage, hasNextPage };
}
